import { Dayjs } from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { OnlineProductOrder } from './online-product-order.js';
import { MapUtils } from '../../utility/map-utils.js';
import { toEnumFromString } from '../../serialization/enum-utils.js';

export class OnlineProductOrderCanceledEvent extends SyncEvent {
  static readonly TYPE_ID: string = '7ce0c3dde643487396ed4993045230f7';

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: Dayjs,
    public readonly source: SyncEventSource,
    public readonly order: OnlineProductOrder,
    public readonly isRefund: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return OnlineProductOrderCanceledEvent.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: Dayjs,
    source: SyncEventSource,
    order: OnlineProductOrder,
    isRefund: boolean,
  }): OnlineProductOrderCanceledEvent {
    return new OnlineProductOrderCanceledEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.order,
        args.isRefund
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): OnlineProductOrderCanceledEvent {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getStringOrNull(map, 'creatorId'),
      deviceId: MapUtils.getStringOrNull(map, 'deviceId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      source: toEnumFromString(MapUtils.getString(map, 'source'), SyncEventSource),
      order: context.fromData<OnlineProductOrder>(MapUtils.getRecord(map, 'order')),
      isRefund: MapUtils.getBoolean(map, 'isRefund'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      deviceId: this.deviceId,
      creationDate: context.encodeDate(this.creationDate),
      source: this.source,
      order: context.toData(this.order),
      isRefund: this.isRefund,
    };
  }

  clone(): this {
    return new OnlineProductOrderCanceledEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        this.creationDate,
        this.source,
        this.order,
        this.isRefund,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
           other.creatorId === this.creatorId &&
           other.deviceId === this.deviceId &&
           other.creationDate.isSame(this.creationDate) &&
           other.source === this.source &&
           other.order.isSame(this.order) &&
           other.isRefund === this.isRefund;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OnlineProductOrderCanceledEvent;
  }

  static assertType(dm: DataModel): dm is OnlineProductOrderCanceledEvent {
    return dm instanceof OnlineProductOrderCanceledEvent;
  }
}

export class OnlineProductOrderCanceledEventBuilder extends DataModelBuilder<OnlineProductOrderCanceledEvent> {
  build(context: SerializationContext, map: SerializedData): OnlineProductOrderCanceledEvent {
    return OnlineProductOrderCanceledEvent.fromMap(context, map);
  }
}
